import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import color from "../../components/utils/Colors";

// ── Validation schema ──────────────────────────────────────────────────────────
const passwordSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Must contain at least one uppercase letter")
    .matches(/[a-z]/, "Must contain at least one lowercase letter")
    .matches(/[0-9]/, "Must contain at least one number")
    .matches(
      /[^A-Za-z0-9]/,
      "Must contain at least one special character"
    )
    .notOneOf(
      [Yup.ref("currentPassword")],
      "New password must be different from current password"
    )
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords do not match")
    .required("Please confirm your new password"),
});

// ── Props ──────────────────────────────────────────────────────────────────────
interface PasswordPopupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────
const PasswordPopup: React.FC<PasswordPopupProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const handleSubmit = async (
    _values: { currentPassword: string; newPassword: string; confirmPassword: string },
    { resetForm }: { resetForm: () => void }
  ) => {
    // TODO: replace with real API call, e.g. await changePassword(values)
    resetForm();
    onSuccess();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: color.textColor, fontWeight: "bold" }}>
        <LockIcon sx={{ marginRight: "8px", verticalAlign: "middle" }} />
        Change Password
      </DialogTitle>

      <Formik
        initialValues={{
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }}
        validationSchema={passwordSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent sx={{ paddingY: "20px" }}>
              <Box sx={{ mb: 1 }}>
                <label>Current Password</label>
              </Box>
              <TextField
                fullWidth
                type="password"
                name="currentPassword"
                margin="normal"
                className="input-root2"
              />

              <Box sx={{ mb: 1, mt: 1 }}>
                <label>New Password</label>
              </Box>
              <TextField
                fullWidth
                type="password"
                name="newPassword"
                margin="normal"
                className="input-root2"
              />

              <Box sx={{ mb: 1, mt: 1 }}>
                <label>Confirm Password</label>
              </Box>
              <TextField
                fullWidth
                type="password"
                name="confirmPassword"
                margin="normal"
                className="input-root2"
              />
            </DialogContent>

            <DialogActions sx={{ padding: "20px" }}>
              <Button
                type="button"
                onClick={onClose}
                sx={{ color: "#999" }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  background: color.firstColor,
                  color: "white",
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:hover": { background: color.forthColor },
                }}
              >
                Update Password
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default PasswordPopup;